/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { ThemeType, UseFormReturn } from "@packages/ui";
import {
  createModal,
  Form,
  showModal,
  standardSchemaResolver,
  useForm,
  useModal,
  useValidator,
} from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import type { Dispatch, SetStateAction } from "react";
import {
  createContext,
  useCallback,
  useContext as useContextReact,
  useMemo,
  useState,
} from "react";
import type z from "zod";

import { useModalResolution } from "../../hooks/useModalResolution";
import type { UseSwitchProfilesProps } from "../../hooks/useSwitchProfiles";
import { ScreenNames } from "../../lib/enums/screenNames";
import { Screens } from "../../lib/enums/screens";
import { nameSchema } from "../../lib/schemas/nameSchema";
import {
  createPinSchemaWithValidation,
  pinSchema,
} from "../../lib/schemas/pinSchema";
import { useRequestMePinExists } from "../../requests/useRequestMePinExists";
import { useRequestMePinPut } from "../../requests/useRequestMePinPut";
import { useRequestRegisterKidsPost } from "../../requests/useRequestRegisterKidsPost";
import { FullscreenModal } from "../blocks/FullscreenModal";
import type { ProfileReadyProps } from "../ProfileReady";
import { SwitchProfiles } from "../SwitchProfiles";
import { CreationHeader } from "./CreationHeader";
import { CreationName } from "./CreationName";
import { CreationPin } from "./CreationPin";

export enum Step {
  Name = "name",
  Pin = "pin",
}

const schemaExists = nameSchema.extend(pinSchema.shape);
type Schema = z.infer<typeof schemaExists>;

const cssDivSpacer: Interpolation = {
  display: "none",
  flexGrow: 1,
  maxHeight: "min(200px, 20dvh)",
  [`@media ${Screens.Large}`]: {
    display: "block",
  },
};
const cssForm: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  maxWidth: 720,
  width: "100%",
};
const cssModalContent: Interpolation<ThemeType> = (theme) => ({
  gap: theme.spacing.l,
});

/** Context */
export type Context = {
  form: UseFormReturn<Schema>;
  step: Step;
  setStep: Dispatch<SetStateAction<Step>>;
};

const context = createContext<Context>({
  form: null,
  step: Step.Name,
  setStep: () => {},
});

export const useContext = () => useContextReact(context);
/** */

export type CreationProps = {
  onSuccess?: (
    data: Awaited<
      ReturnType<ReturnType<typeof useRequestRegisterKidsPost>["mutateAsync"]>
    >,
  ) => void;
  switchProfilesProps?: UseSwitchProfilesProps;
  profileReadyProps?: ProfileReadyProps;
};

const Internal = ({ onSuccess, switchProfilesProps }: CreationProps) => {
  const modal = useModal();

  const { track } = useAnalytics();
  const { form, setStep } = useContext();
  const query = useRequestMePinExists();
  const mutationPost = useRequestRegisterKidsPost();
  const mutationPut = useRequestMePinPut();

  const onClose = useCallback(() => {
    form.reset();
    setStep(Step.Name);
  }, [form.reset]);
  const { onOpenChange, resolve } = useModalResolution({
    modal,
    onClose,
  });

  const name = form.watch("name");

  const screenNamePin = useMemo(() => {
    return query.data?.exists
      ? ScreenNames.ParentPinInput
      : ScreenNames.KidsProfileCreationParentPin;
  }, [query.data?.exists]);

  const onSubmit = useCallback(
    async (data: Schema) => {
      const splitNameData = {
        pin: data.pin,
        name: data.name.split(" ")[0],
        last_name: data.name.split(" ").slice(1).join(" "),
      };
      try {
        if (!query.data?.exists) {
          const response = await mutationPut.mutateAsync({
            body: splitNameData,
          });
          if (!response.success) return;
        }

        const response = await mutationPost.mutateAsync({
          body: splitNameData,
        });
        if (response) {
          track({
            event: HallowAnalyticsEvent.CompletedKidsProfileCreationStep,
            properties: {
              first_name: name,
              screen_name: screenNamePin,
            },
          });

          onSuccess?.(response);

          resolve(true, () => {
            showModal(SwitchProfiles, {
              ...switchProfilesProps,
              isShowProfileReady: true,
            });
            onClose();
            modal.remove();
          });
        }
      } catch {
        track({
          event: HallowAnalyticsEvent.EnteredIncorrectParentPIN,
          properties: {
            error: "incorrect_pin",
            screen_name: screenNamePin,
          },
        });
      }
    },
    [
      modal.remove,
      mutationPost.mutateAsync,
      mutationPut.mutateAsync,
      name,
      onClose,
      onSuccess,
      query.data,
      resolve,
      screenNamePin,
      switchProfilesProps,
      track,
    ],
  );

  return (
    <FullscreenModal
      contentCss={cssModalContent}
      isOpen={modal.visible}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        document.getElementById("name")?.focus();
      }}
      onOpenChange={onOpenChange}
    >
      <VisuallyHidden>
        <RadixUIDialog.Description>
          <I18nMessage message="kids_profile_creation_accessibility_label" />
        </RadixUIDialog.Description>
        <RadixUIDialog.Title>
          <I18nMessage message="kids_profile_creation_accessibility_label" />
        </RadixUIDialog.Title>
      </VisuallyHidden>
      <CreationHeader />
      <Form
        formCSS={cssForm}
        formProps={{ onSubmit: form.handleSubmit(onSubmit) }}
        {...form}
      >
        <div css={cssDivSpacer} />
        <CreationName />
        <CreationPin
          pinError={mutationPost.error?.message || mutationPut.error?.message}
        />
      </Form>
    </FullscreenModal>
  );
};

export const Creation = createModal((props: CreationProps) => {
  const query = useRequestMePinExists();
  const [step, setStep] = useState<Step>(Step.Name);
  const t = useTranslations();

  const schema = useMemo(() => {
    if (query.data?.exists) return schemaExists;
    else return nameSchema.extend(createPinSchemaWithValidation({ t }).shape);
  }, [query.data?.exists, t]);

  const { resolver } = useValidator<Schema>(standardSchemaResolver(schema));
  const form = useForm<Schema>({
    defaultValues: { name: "", pin: "" },
    mode: "onChange",
    resolver,
  });

  return (
    <context.Provider value={{ form, step, setStep }}>
      <Internal {...props} />
    </context.Provider>
  );
});
